import { useRef, useState } from "react";
import { useReveal } from "../hooks/useReveal";

import ownerPhoto from "../assets/tio-pipe-foto.webp";
const ownerPhotoSrc = ownerPhoto;

const historyStats = [
    { v: "17+", l: "Years" },
    { v: "500+", l: "Clients" },
    { v: "100%", l: "Quality" },
];

export function About() {
    const revealRef = useReveal<HTMLDivElement>();
    const scrollRef = useRef<HTMLDivElement>(null);
    const [activeSlide, setActiveSlide] = useState(0);

    const goToSlide = (index: number) => {
        const scroller = scrollRef.current;
        if (!scroller) return;

        scroller.scrollTo({
            left: scroller.clientWidth * index,
            behavior: "smooth",
        });
        setActiveSlide(index);
    };

    const handleScroll = () => {
        const scroller = scrollRef.current;
        if (!scroller) return;

        setActiveSlide(Math.round(scroller.scrollLeft / scroller.clientWidth));
    };

    return (
        <section
            id="about"
            className="relative overflow-hidden bg-white text-slate-900"
        >
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(37,87,199,0.10),transparent_28%),radial-gradient(circle_at_80%_80%,rgba(245,165,32,0.10),transparent_30%)]" />

            <div ref={revealRef} className="reveal relative z-10 py-12 pb-20 md:py-16 md:pb-24">
                <div
                    ref={scrollRef}
                    onScroll={handleScroll}
                    className="about-scroll flex snap-x snap-mandatory overflow-x-auto scroll-smooth"
                    style={{
                        scrollbarWidth: "none",
                        msOverflowStyle: "none",
                    }}
                >
                    <article className="w-full shrink-0 snap-start">
                        <div className="history-split-layout grid grid-cols-1 gap-8 px-6 lg:items-center lg:gap-12 lg:px-0">
                            <div className="relative z-10">
                                <img
                                    src={ownerPhotoSrc}
                                    alt="Power One Electric — Our team"
                                    className="h-[360px] w-full object-contain object-left md:h-[460px]"
                                />
                            </div>

                            <div className="relative z-10 lg:pr-[max(1.5rem,calc((100vw-88rem)/2+1.5rem))]">
                                <div className="mb-4 h-1 w-10 bg-[#2557C7]" />
                                <h2 className="mt-3 text-4xl font-black leading-tight text-slate-900 md:text-5xl">
                                    Our History
                                </h2>
                                <div className="mt-5 space-y-4 text-base leading-relaxed text-slate-600 md:text-[17px]">
                                    <p>
                                        Power One Electric was born from adversity. In the wake of Hurricane Katrina's devastation, New Orleans and the surrounding communities were left in darkness — not just literally, but structurally, needing reliable expertise to rebuild what was lost. In 2008, Power One Electric was founded right here in the New Orleans, Louisiana area with a singular purpose: to provide an immediate, dependable solution to electrical problems when it matters most.
                                    </p>
                                    <p>
                                        What began as a response to crisis has grown into a trusted electrical contractor serving both commercial and residential clients across projects of medium and large scale. For over 15 years, we have built our reputation one project at a time — delivering quality workmanship, uncompromising safety standards, and the kind of reliability that our community depends on.
                                    </p>
                                </div>

                                <div className="mt-6 grid grid-cols-3 gap-4 border-t border-slate-100 pt-5">
                                    {historyStats.map((stat, statIndex) => (
                                        <div key={stat.l} className={`text-center stat-item delay-${(statIndex + 1) * 100}`}>
                                            <div className="text-2xl font-black text-[#2557C7]">{stat.v}</div>
                                            <div className="mt-1 text-xs uppercase tracking-wider text-slate-400">{stat.l}</div>
                                        </div>
                                    ))}
                                </div>

                                <div className="mt-6 flex flex-wrap items-center gap-4">
                                    <a
                                        href="#contact"
                                        className="inline-flex items-center gap-2 rounded-xl bg-[#2557C7] px-6 py-3 font-bold text-white transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg"
                                    >
                                        Contact Us
                                    </a>
                                </div>
                            </div>
                        </div>
                    </article>

                    <article className="relative flex w-full shrink-0 snap-start overflow-hidden pt-2 md:pt-8 lg:pt-14">
                        <svg
                            className="pointer-events-none absolute left-1/2 top-1/2 h-[300px] w-[300px] -translate-x-1/2 -translate-y-1/2 text-[#2557C7] opacity-[0.12] md:h-[520px] md:w-[520px] md:opacity-[0.14]"
                            viewBox="0 0 64 64"
                            fill="none"
                            aria-hidden="true"
                        >
                            <path
                                d="M44 18A20 20 0 1 1 20 18"
                                stroke="currentColor"
                                strokeWidth="7"
                                strokeLinecap="round"
                            />
                            <rect x="27" y="6" width="10" height="26" rx="5" fill="currentColor" />
                        </svg>

                        <div className="mx-auto flex w-full max-w-352 items-stretch px-6 md:min-h-[420px]">
                            <div className="relative z-10 grid min-h-full w-full grid-cols-1 content-around gap-6 py-4 lg:grid-cols-2 lg:content-center lg:items-center lg:gap-32 lg:py-0 xl:gap-44">
                                <div className="flex flex-col justify-center">
                                    <h2 className="text-3xl font-black leading-tight text-slate-900 md:text-6xl">
                                        Our Mission
                                    </h2>
                                    <p className="mt-4 text-base leading-relaxed text-slate-600 md:mt-6 md:text-xl">
                                        To deliver expert commercial electrical solutions across Louisiana with integrity, precision, and accountability. We partner with general contractors, developers, businesses, and institutions to complete projects safely, efficiently, and to the highest standards of craftsmanship. From preconstruction through project completion, we are committed to building lasting relationships by delivering dependable service, practical solutions, and exceptional results.
                                    </p>
                                </div>

                                <div className="flex flex-col justify-center">
                                    <h2 className="text-3xl font-black leading-tight text-slate-900 md:text-6xl">
                                        Our Vision
                                    </h2>
                                    <p className="mt-4 text-base leading-relaxed text-slate-600 md:mt-6 md:text-xl">
                                        To be Louisiana’s most trusted commercial electrical contractor, recognized for delivering exceptional workmanship, innovative solutions, and reliable service. We strive to set the standard for professionalism in our industry by investing in our people, embracing new technologies, and building lasting partnerships that help power Louisiana’s growth.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </article>
                </div>
            </div>

            <div className="absolute bottom-5 left-1/2 z-20 flex -translate-x-1/2 items-center gap-3">
                <div className="relative grid w-[min(22rem,calc(100vw-3rem))] grid-cols-2 rounded-full bg-white/85 p-1 shadow-lg shadow-slate-200/70 backdrop-blur">
                    <span
                        className={`absolute inset-y-1 left-1 w-[calc(50%-0.25rem)] rounded-full bg-[#2557C7] shadow-md transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${
                            activeSlide === 1 ? "translate-x-full" : "translate-x-0"
                        }`}
                    />
                    {["History", "Mission & Vision"].map((label, index) => (
                        <button
                            key={label}
                            type="button"
                            onClick={() => goToSlide(index)}
                            className={`relative z-10 rounded-full px-4 py-2 text-xs font-black uppercase tracking-wider transition-colors duration-300 ${
                                activeSlide === index ? "text-white" : "text-slate-500 hover:text-slate-900"
                            }`}
                            aria-label={`Go to ${label}`}
                        >
                            {label}
                        </button>
                    ))}
                </div>
            </div>
        </section>
    );
}
